/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderIpAddrList, renderSiteList, renderStringList } from '../../properties/Properties';
import { useServer, timeRangeFilterId, serverFilterId } from '../../filters';
import { getMsOf } from '../../../utilities/TimeRangeNavigationUtilities';
import { useFilterState } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import { stringProperty, listProperty, listLinkProperty } from '../../properties/funcs';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';

const LOOPBACK_IP = '127.0.0.1';

const propertiesColumns = [
    {
        properties: [
            stringProperty(t`Computer Name`, 'computerName'),
            stringProperty(t`IP Address`, 'ipAddress'),
            stringProperty(t`Computer Description`, 'description'),
            stringProperty(t`Operating System`, 'operatingSystem'),
            stringProperty(t`Up Time`, 'upTime'),
            stringProperty(t`Processor`, 'processor'),
            stringProperty(t`Installed RAM`, 'installedMemory'),
            listProperty(t`NICs`, 'nics', renderStringList),
            listLinkProperty(t`Sites`, 'sites', [], [serverFilterId], renderSiteList),
        ],
    },
    {
        properties: [listProperty(t`IP Addresses`, 'ipAddresses', renderIpAddrList)],
    },
];

/**
 * Convert the array of property name/value pairs into an object, then convert ipAddresses and loopbackIps from
 * pseudo-JSON strings into string arrays. Annotate any IPs in ipAddresses which are loopbacks, and remove the
 * localhost loopback from the list if present. Finally, include sites in the main set of properties.
 * @param data
 * @param sites
 * @returns {*}
 */
function buildPropertiesData(data, sites) {
    const properties = _(data).keyBy('name').mapValues('value').value();
    const loopbacks = decodeStringList(properties.loopbackIps);
    properties.ipAddresses = _.map(_.pull(decodeStringList(properties.ipAddresses), LOOPBACK_IP), (ip) => {
        return { ipAddress: ip, loopback: loopbacks.indexOf(ip) >= 0 };
    });
    properties.sites = sites;
    return properties;
}

function decodeStringList(str) {
    const list = str ? str.replace(/[[\]]/g, '') : null;
    if (!list) return [];
    return list.split(/,[ ]*/);
}

/**
 * Combine data from serverinfo and device queries
 * @returns {Object}
 */
function usePropertiesData() {
    const server = useServer();
    const [filterState] = useFilterState();
    const dataService = useDataService();
    const [props, setProps] = useState();
    const timeRange = filterState?.current?.[timeRangeFilterId];
    const serverId = server?.uuid;
    const start = timeRange?.start;
    const end = timeRange?.end;

    useEffect(() => {
        if (!serverId || !start) {
            return;
        }
        const serverInfoParams = {
            start: getMsOf(start),
            end: getMsOf(end),
            type: 'server',
            server: serverId,
        };
        const serverInfoRequest = dataService.get('/query/serverinfo', serverInfoParams);
        const deviceRequest = dataService.get(`/ngp/v2/devices/${serverId}`);

        Promise.all([serverInfoRequest, deviceRequest]).then((resolved) => {
            const [serverInfo, deviceData] = resolved;
            setProps(buildPropertiesData(serverInfo?.data, deviceData?.sites));
        });
    }, [dataService, serverId, start, end]);

    return props;
}

export function ServerProperties({ data }) {
    const classes = useStyles();
    return (
        <div className={classes.propertiesPanel}>
            {propertiesColumns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function ServerPropertiesPanel() {
    const properties = usePropertiesData();
    if (!properties) return null;
    return <ServerProperties data={properties} />;
}

ServerPropertiesPanel.propTypes = panelPropTypes;

ServerPropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Server Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 250,
};

export default Panel({
    typeAlias: 'ServerInfo',
    filters: {
        required: [timeRangeFilterId, serverFilterId],
        optional: [],
    },
})(ServerPropertiesPanel);
